// v14au — Gedeelde verzendlogica voor offerte-e-mails: idempotente verzending,
// eerlijke statuslabels (SMTP-aanvaard ≠ afgeleverd), historiek en 'Verstuurd'-vraag.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertCircle, History, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { setProjectQuoteStatus } from "../../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export const DISPATCH_LABELS = {
  sending: { text: "Bezig met verzenden", cls: "bg-[#F0EFEA] text-[#6E6E6C]" },
  smtp_accepted: { text: "Aanvaard door je mailserver – aflevering niet bevestigd", cls: "bg-[#EEF3EC] text-[#3f5b3b]" },
  smtp_rejected: { text: "Geweigerd door je mailserver", cls: "bg-red-50 text-red-700" },
  failed: { text: "Niet verzonden", cls: "bg-red-50 text-red-700" },
  unknown: { text: "Status onbekend – controleer je Verzonden-map", cls: "bg-amber-50 text-amber-800" },
  delivered: { text: "Afgeleverd", cls: "bg-[#EEF3EC] text-[#3f5b3b]" },
  bounced: { text: "Teruggekaatst", cls: "bg-red-50 text-red-700" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-BE", { dateStyle: "short", timeStyle: "short" }) : "");

export function useQuoteEmailSend(quoteId, { onQuoteStatusChange, onFailed } = {}) {
  const keyRef = useRef(newKey());
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [needUnknownConfirm, setNeedUnknownConfirm] = useState(false);
  const [askMarkSent, setAskMarkSent] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!quoteId) return;
    try {
      const r = await fetch(`${API}/quotes/${quoteId}/email-dispatches`, { credentials: "include" });
      if (r.ok) setHistory(await r.json());
    } catch { /* stil */ }
  }, [quoteId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = async ({ to, subject, body, confirmUnknown = false }) => {
    if (sending) return { ok: false };
    const prev = history.find((h) => h.state === "smtp_accepted" && (h.to || "").toLowerCase() === (to || "").toLowerCase());
    if (prev && !confirmUnknown && !window.confirm(
      `Deze offerte werd op ${fmt(prev.created_at)} al aanvaard door je mailserver voor ${prev.to}. Opnieuw verzenden?`)) {
      return { ok: false, cancelled: true };
    }
    setSending(true); setNeedUnknownConfirm(false);
    try {
      const r = await fetch(`${API}/quotes/${quoteId}/send-email`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, subject, body, idempotency_key: keyRef.current, confirm_resend_after_unknown: confirmUnknown }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409 && d.detail?.code === "unknown_previous_send") {
        setNeedUnknownConfirm(true);
        return { ok: false, needsConfirm: true };
      }
      if (r.status !== 409) keyRef.current = newKey();
      if (!r.ok) {
        onFailed && onFailed();
        return { ok: false, message: (typeof d.detail === "string" ? d.detail : d.detail?.message) || `Verzenden mislukt (${r.status})` };
      }
      if (d.duplicate && d.state === "sending") return { ok: false, message: "Deze verzending loopt nog — bekijk de verzendhistoriek" };
      if (!d.ok) return { ok: false, message: d.message || "Verzending niet aanvaard" };
      if (d.quote_status === "draft" && d.project_id) setAskMarkSent({ projectId: d.project_id });
      return { ok: true, message: d.message, attachment: d.attachment };
    } catch {
      return { ok: false, message: "Netwerkfout — controleer de verzendhistoriek voor je opnieuw verzendt" };
    } finally {
      setSending(false);
      loadHistory();
    }
  };

  const markSent = async () => {
    try {
      await setProjectQuoteStatus(askMarkSent.projectId, "sent");
      onQuoteStatusChange && onQuoteStatusChange("sent");
      toast.success("Offerte op 'Verstuurd' gezet");
    } catch { toast.error("Status wijzigen mislukt"); }
    setAskMarkSent(null);
  };

  return { send, sending, history, needUnknownConfirm, askMarkSent, markSent, dismissMarkSent: () => setAskMarkSent(null) };
}

export function EmailSendPrompts({ ctl, onConfirmUnknown, prefix }) {
  return (
    <>
      {ctl.needUnknownConfirm && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-2" data-testid={`${prefix}-unknown-warning`}>
          <div className="flex items-start gap-2 text-amber-900">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Een vorige verzending naar dit adres heeft een onbekende status. Controleer eerst je Verzonden-map, zodat de klant de offerte niet dubbel ontvangt.
          </div>
          <Button size="sm" variant="outline" onClick={onConfirmUnknown} disabled={ctl.sending} data-testid={`${prefix}-unknown-confirm-btn`}>
            Gecontroleerd — toch opnieuw verzenden
          </Button>
        </div>
      )}
      {ctl.askMarkSent && (
        <div className="text-xs bg-[#FAF9F6] border border-[#E5E2DC] rounded-lg px-3 py-2 flex flex-wrap items-center gap-2" data-testid={`${prefix}-mark-sent-prompt`}>
          <span className="flex-1 min-w-[180px]">Aanvaard door je mailserver. Offerte op <b>Verstuurd</b> zetten?</span>
          <Button size="sm" onClick={ctl.markSent} className="bg-[#5D7259] hover:bg-[#4a5c47] text-white" data-testid={`${prefix}-mark-sent-yes`}>Ja</Button>
          <Button size="sm" variant="outline" onClick={ctl.dismissMarkSent} data-testid={`${prefix}-mark-sent-no`}>Nee</Button>
        </div>
      )}
    </>
  );
}

export function EmailDispatchHistory({ rows, prefix }) {
  const [open, setOpen] = useState(false);
  if (!rows?.length) return null;
  return (
    <div className="text-xs border border-[#F0EFEA] rounded-lg" data-testid={`${prefix}-history`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left font-semibold text-[#5D7259]"
        data-testid={`${prefix}-history-toggle`}>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`} />
        <History className="w-3 h-3" /> Verzendhistoriek ({rows.length})
      </button>
      {open && (
        <ul className="px-3 pb-2 space-y-1.5">
          {rows.map((h) => {
            const L = DISPATCH_LABELS[h.state] || { text: h.state, cls: "bg-[#F0EFEA]" };
            return (
              <li key={h.id} className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-history-row`}>
                <span className="text-[#6E6E6C]">{fmt(h.created_at)}</span>
                <span className="font-mono">{h.to}</span>
                <span className={`rounded-full px-2 py-0.5 ${L.cls}`} data-testid={`${prefix}-history-state-${h.state}`}>{L.text}</span>
                {h.state !== "smtp_accepted" && h.message && <span className="text-[#6E6E6C] w-full">{h.message}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function SmtpStatusHints({ cfg, prefix }) {
  if (!cfg?.connected) return null;
  return (
    <>
      {cfg.status !== "verified" && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900" data-testid={`${prefix}-smtp-unverified`}>
          {cfg.status === "failed"
            ? `Laatste mailbox-test mislukt: ${cfg.last_test_message || "onbekende fout"}. Controleer je instellingen.`
            : "Je mailboxinstellingen zijn nog niet getest. Test ze in Instellingen → E-mail."}
        </div>
      )}
      {cfg.sender_warning && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-900" data-testid={`${prefix}-sender-warning`}>
          {cfg.sender_warning}
        </div>
      )}
    </>
  );
}
